import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ImageUpload } from '@/components/ImageUpload';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { PhotoGallery } from '@/model';
import { usePutPhotoGalleryMutation } from '@/services/queries/photo-gallery';
import { PutPhotoGalleryRequest } from '@/services/request/photo-gallery';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PhotoGalleryEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: PhotoGallery;
  onClose: () => void;
  onDone?: () => void;
}

export const PhotoGalleryEditModal = ({ isOpen, onClose, item, onDone }: PhotoGalleryEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutPhotoGalleryMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      title: item.title,
      description: item.description,
      file: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutPhotoGalleryRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        title: data.title,
        description: data.description,
        credit: data.credit,
        image: data.file,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_team_label')}
              placeholder={t(
                'pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_title_label')}
              {...register('title')}
              helperText={t(
                'pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_title_helper',
              )}
              maxLength={200}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_description_label',
              )}
              {...register('description')}
              helperText={t(
                'pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_description_helper',
              )}
              maxLength={200}
              rootProps={{ isInvalid: !!errors?.description, errorMessage: errors?.description?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_credit_label')}
              {...register('credit')}
              maxLength={50}
              rootProps={{ isInvalid: !!errors?.credit, errorMessage: errors?.credit?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_image_label')}
                imageURL={item.image_url}
                onFileChange={file => setValue('file', file)}
                helpText={t(
                  'pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_image_helper',
                )}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_history_gallery.components.photo_gallery_edit_modal.photo_gallery_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
